import DOMPurify, { type Config as DOMPurifyConfig } from "dompurify";
import type { Attachment } from "@onemileauto/shared-types";

/**
 * Security-critical path: inbound email HTML is hostile, untrusted input
 * (per docs/architecture/mail-architecture.md, "HTML rendering"). Two
 * independent layers, neither alone sufficient:
 *
 *  1. DOMPurify sanitizes the HTML string itself (strips <script>, event
 *     handler attributes, javascript:/data: URLs used as script vectors,
 *     <iframe>/<object>/<embed>/<form>, etc.) before it ever becomes DOM.
 *  2. The caller (components/mail/safe-html-frame.tsx) renders the
 *     sanitized output inside `<iframe sandbox="allow-same-origin">` with
 *     NO `allow-scripts` token. That sandbox is the real backstop: even a
 *     sanitizer bypass can't execute script in a frame that is forbidden
 *     from running any, and `allow-same-origin` alone (no allow-scripts)
 *     is still safe because script execution — not "same-origin-ness" —
 *     is the thing that actually matters for XSS.
 *
 * Do not relax the DOMPurify config or the sandbox token list without a
 * deliberate security review — see the architecture doc.
 */

const SANITIZE_CONFIG: DOMPurifyConfig = {
  WHOLE_DOCUMENT: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Explicit allowlist of URI schemes usable in href/src/etc. Blocks
  // javascript:, data: (SVG/data-URI XSS vectors), vbscript:, etc.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "link", "meta", "base", "audio", "video"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "srcset"],
};

/**
 * Inline images in real-world inbound HTML reference their attachment via
 * `cid:<content-id>` rather than a fetchable URL — postal-mime (the MIME
 * parser, see lib/mime-parse.ts on the Worker) does not rewrite these since
 * it runs server-side with no concept of our attachment API. Rewrite them
 * to the real attachment endpoint here, before sanitization, using the
 * message's own attachment list (matched by Content-ID) so DOMPurify sees
 * (and allows, per ALLOWED_URI_REGEXP above) an ordinary same-origin path.
 */
function rewriteCidReferences(html: string, attachments: Attachment[], mailboxId: string): string {
  const byContentId = new Map<string, Attachment>();
  for (const att of attachments) {
    if (!att.contentId) continue;
    byContentId.set(att.contentId.replace(/^</, "").replace(/>$/, ""), att);
  }
  if (byContentId.size === 0) return html;

  return html.replace(/((?:src|background)\s*=\s*)(["'])cid:([^"']+)\2/gi, (match, attr: string, quote: string, cid: string) => {
    const attachment = byContentId.get(cid.trim());
    if (!attachment) return match;
    return `${attr}${quote}/api/mailboxes/${mailboxId}/attachments/${attachment.id}${quote}`;
  });
}

function wrapDocument(bodyHtml: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
  :root { color-scheme: light; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 15px;
    line-height: 1.55;
    color: #2a2119;
    /* Matches the plain-text fallback body's Tailwind px-4 py-4 (message-
       card.tsx) so HTML and plain-text messages share the same rhythm
       instead of HTML content sitting flush against the card edges. */
    padding: 16px;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  img, video { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #a25f27; }
  * { box-sizing: border-box; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`;
}

/**
 * Sanitize an inbound message's HTML body and wrap it as a full document
 * suitable for an `<iframe srcDoc>`. Returns null for empty input so the
 * caller can fall back to the plain-text body instead.
 */
export function sanitizeEmailHtml(rawHtml: string, attachments: Attachment[], mailboxId: string): string {
  const withResolvedImages = rewriteCidReferences(rawHtml, attachments, mailboxId);
  const clean = DOMPurify.sanitize(withResolvedImages, SANITIZE_CONFIG);
  return wrapDocument(clean);
}
